// Stage 2 of the Smart Asset Import pipeline: deterministic structure detection.
//
// Input is a raw grid (array-of-arrays) exactly as produced by
// XLSX.utils.sheet_to_json(ws, { header: 1 }) or Papa.parse(..., { header: false }).
// We do NOT assume the header is on row 1 — real client files have title rows, blank
// rows, and merged banners above the real header.
//
// Everything here is pure and deterministic so it can be golden-file unit-tested with
// no network and no AI.

import { classifyCell, cleanString } from './textNormalize';

const isBlank = (v) => v === null || v === undefined || String(v).trim() === '';

const looksLikeNumber = (v) => {
  if (typeof v === 'number') return true;
  if (isBlank(v)) return false;
  return /^-?\d+(\.\d+)?$/.test(String(v).trim());
};

const looksLikeDate = (v) => {
  if (v instanceof Date) return true;
  if (isBlank(v)) return false;
  const s = String(v).trim();
  // dd.mm.yyyy, dd/mm/yyyy, dd-mm-yyyy, or ISO-ish
  return /^\d{1,4}[./-]\d{1,2}[./-]\d{1,4}/.test(s);
};

/**
 * Score a candidate header row. Headers tend to be short, text-y, non-numeric, distinct,
 * and densely filled. Returns a numeric score (higher = more header-like).
 */
const scoreHeaderRow = (row) => {
  if (!Array.isArray(row)) return 0;
  const nonEmpty = row.filter((c) => !isBlank(c));
  if (nonEmpty.length === 0) return 0;

  let textCells = 0;
  nonEmpty.forEach((c) => {
    if (!looksLikeNumber(c) && !looksLikeDate(c)) textCells += 1;
  });
  const textRatio = textCells / nonEmpty.length;

  // Distinct, reasonably short labels are header-like.
  const distinct = new Set(nonEmpty.map((c) => cleanString(c).toLowerCase()));
  const distinctRatio = distinct.size / nonEmpty.length;
  const shortLabels = nonEmpty.filter((c) => cleanString(c).length > 0 && cleanString(c).length <= 40).length;
  const shortRatio = shortLabels / nonEmpty.length;

  // Weight density (nonEmpty count) but heavily reward text/distinct/short signals.
  return nonEmpty.length * (textRatio * 0.6 + distinctRatio * 0.25 + shortRatio * 0.15);
};

const nonEmptyCount = (row) => (Array.isArray(row) ? row.filter((c) => !isBlank(c)).length : 0);

/**
 * Detect the header row within the first `maxScan` rows.
 *
 * Headers precede data, so we pick the EARLIEST row that scores within 85% of the best
 * candidate (rather than the global max, which a coincidentally text-heavy data row could
 * win). We only flag `ambiguous` when there is genuine uncertainty: no row scores like a
 * header at all, or the strongest candidate sits well below the chosen row (suggesting the
 * real header is further down).
 *
 * @returns {{ index:number, ambiguous:boolean }}
 */
export const detectHeaderRow = (grid, maxScan = 12) => {
  if (!Array.isArray(grid) || grid.length === 0) return { index: -1, ambiguous: true };
  const limit = Math.min(maxScan, grid.length);
  const scores = [];
  let maxScore = -1;
  let maxIdx = 0;
  for (let i = 0; i < limit; i += 1) {
    const s = scoreHeaderRow(grid[i]);
    scores.push(s);
    if (s > maxScore) { maxScore = s; maxIdx = i; }
  }

  // Earliest row that is "strong enough" and has at least a couple of labels.
  const threshold = maxScore * 0.85;
  let chosen = maxIdx;
  for (let i = 0; i < limit; i += 1) {
    if (scores[i] >= threshold && nonEmptyCount(grid[i]) >= 2) { chosen = i; break; }
  }

  // Ambiguous only when there's real uncertainty about where the header is.
  const ambiguous = maxScore < 2 || (chosen !== maxIdx && scores[chosen] < maxScore * 0.7);
  return { index: chosen, ambiguous };
};

/**
 * Profile the whole sheet: detect the header row, build column profiles, drop fully
 * empty columns, and flag separator rows.
 *
 * @param {Array<Array<*>>} grid  raw array-of-arrays
 * @returns {{
 *   headerRowIndex:number, headerAmbiguous:boolean,
 *   headers:Array<{index:number, raw:string}>,    // kept (non-empty) columns only
 *   keptColumnIndexes:number[],
 *   droppedEmptyColumns:Array<{index:number, raw:string}>,
 *   dataRows:Array<{ sourceRowIndex:number, cells:Array<*>, isSeparator:boolean }>,
 *   columns:Array<{index:number, header:string, fillRate:number, distinctCount:number,
 *                  types:{number:number,date:number,text:number,empty:number}, allEmpty:boolean}>
 * }}
 */
export const profileSheet = (grid) => {
  const empty = {
    headerRowIndex: -1, headerAmbiguous: true, headers: [], keptColumnIndexes: [],
    droppedEmptyColumns: [], dataRows: [], columns: [],
  };
  if (!Array.isArray(grid) || grid.length === 0) return empty;

  const { index: headerRowIndex, ambiguous: headerAmbiguous } = detectHeaderRow(grid);
  const headerRow = grid[headerRowIndex] || [];
  const bodyRows = grid.slice(headerRowIndex + 1);

  const colCount = Math.max(
    headerRow.length,
    ...bodyRows.map((r) => (Array.isArray(r) ? r.length : 0)),
    0
  );

  // Per-column profiling over body rows.
  const columns = [];
  const keptColumnIndexes = [];
  const droppedEmptyColumns = [];

  for (let c = 0; c < colCount; c += 1) {
    const rawHeader = cleanString(headerRow[c]);
    const values = bodyRows.map((r) => (Array.isArray(r) ? r[c] : undefined));
    const types = { number: 0, date: 0, text: 0, empty: 0 };
    const distinct = new Set();
    let filled = 0;
    values.forEach((v) => {
      if (isBlank(v)) {
        types.empty += 1;
        return;
      }
      filled += 1;
      distinct.add(cleanString(v).toLowerCase());
      if (looksLikeNumber(v)) types.number += 1;
      else if (looksLikeDate(v)) types.date += 1;
      else types.text += 1;
    });
    const total = values.length || 1;
    // Drop any column that carries no DATA, even if it has a header. The real client files
    // ship header-only placeholder columns (e.g. "Existing Asset Code as per AssetPro") that
    // are 100% empty; importing them adds nothing. We still record the dropped header so the
    // user can see exactly what was ignored (nothing is silently lost).
    const allEmpty = filled === 0;
    const col = {
      index: c,
      header: rawHeader,
      fillRate: filled / total,
      distinctCount: distinct.size,
      types,
      allEmpty,
    };
    columns.push(col);
    if (allEmpty) droppedEmptyColumns.push({ index: c, raw: rawHeader });
    else keptColumnIndexes.push(c);
  }

  const headers = keptColumnIndexes.map((c) => ({ index: c, raw: cleanString(headerRow[c]) }));

  // Build data rows, flagging separator rows (e.g. a single merged label, or near-empty).
  const dataRows = bodyRows.map((r, i) => {
    const cells = Array.isArray(r) ? r : [];
    const nonEmptyKept = keptColumnIndexes.filter((c) => !isBlank(cells[c]));
    // Separator: zero or one kept cell filled while the row is not the only data.
    const isSeparator = nonEmptyKept.length <= 1 && keptColumnIndexes.length > 2;
    return { sourceRowIndex: headerRowIndex + 1 + i, cells, isSeparator };
  });

  return {
    headerRowIndex,
    headerAmbiguous,
    headers,
    keptColumnIndexes,
    droppedEmptyColumns,
    dataRows,
    columns: columns.filter((col) => keptColumnIndexes.includes(col.index)),
  };
};

export { classifyCell };
